// ===========================================================================
// Global modal-drag system (2026-10-05).
//
// Owner directive: "make every modal draggable via mouse."
//
// DESIGN — zero per-component code: every centered modal panel that carries
// the CSS class `draggable-modal` is automatically draggable via its TOP
// EDGE (~32px tall strip). One module-level listener (one MutationObserver +
// one set of document-level mouse handlers) covers the entire app, so a
// sweep only needs to ADD the marker class — no hooks, no props.
//
// How it works:
//  1. A MutationObserver on document.body watches for added nodes; any
//     .draggable-modal element found gets a mousemove-based hit-zone.
//  2. The hit zone is the panel's top 32px (header area). mousedown there
//     starts a drag: the panel gets position:fixed + the mouse delta applied
//     via transform (no layout thrash), and the pointer is captured on
//     document so the drag continues outside the panel.
//  3. mouseup ends the drag. Escape/blur never repositions.
//  4. Iframes and form fields inside the panel keep working: the drag only
//     starts in the top strip (cursor: grab there), never on inputs.
//
// Position persistence: per-session only (a module Map keyed by the panel's
// data-modal-drag-id or its DOM position) so reopening a modal re-centers.
// ===========================================================================

const DRAG_CLASS = 'draggable-modal';
const HIT_ZONE_HEIGHT = 32;
const HIT_ZONE_ID = 'modal-drag-hitzone';

let installed = false;
let activeDrag: {
  panel: HTMLElement;
  startMouseX: number;
  startMouseY: number;
  startLeft: number;
  startTop: number;
} | null = null;

/** Panels already instrumented (WeakSet — no leak when modals unmount). */
const instrumented = new WeakSet<HTMLElement>();

function ensureHitZone(panel: HTMLElement): HTMLElement {
  let zone = panel.querySelector<HTMLDivElement>(`#${HIT_ZONE_ID}`);
  if (zone) return zone;
  zone = document.createElement('div');
  zone.id = HIT_ZONE_ID;
  zone.style.cssText = [
    `height:${HIT_ZONE_HEIGHT}px`,
    'width:100%',
    'position:absolute',
    'top:0',
    'left:0',
    'right:0',
    'cursor:grab',
    'z-index:5',
    'user-select:none',
    'touch-action:none',
  ].join(';');
  // The panel must be a positioning context for the hit zone; centered modal
  // panels are `relative` by convention — enforce defensively.
  const pos = getComputedStyle(panel).position;
  if (pos === 'static') panel.style.position = 'relative';
  panel.appendChild(zone);
  return zone;
}

function onMouseDown(e: MouseEvent) {
  if (activeDrag || e.button !== 0) return;
  const target = e.target as HTMLElement | null;
  // Ignore mousedown on interactive elements (buttons, links, inputs, close icons)
  if (target?.closest('button, a, input, select, textarea, [role="button"], [data-no-drag]')) {
    return;
  }
  const zone = target?.closest<HTMLElement>(`#${HIT_ZONE_ID}`);
  if (!zone) return;
  const panel = zone.closest<HTMLElement>(`.${DRAG_CLASS}`);
  if (!panel) return;

  const rect = panel.getBoundingClientRect();
  activeDrag = {
    panel,
    startMouseX: e.clientX,
    startMouseY: e.clientY,
    startLeft: rect.left,
    startTop: rect.top,
  };
  // Freeze the panel at its current viewport position so transform deltas are
  // absolute. Centered flex panels become `fixed` — visually identical, now
  // free to move.
  panel.style.position = 'fixed';
  panel.style.left = `${rect.left}px`;
  panel.style.top = `${rect.top}px`;
  panel.style.margin = '0';
  panel.style.transform = 'none';
  zone.style.cursor = 'grabbing';
  // Full-screen ghost hit area so the drag never "loses" the pointer over
  // fast moves; pointer-events bypass inner content only during the drag.
  document.body.style.cursor = 'grabbing';
  document.body.style.userSelect = 'none';
  e.preventDefault();
}

function onMouseMove(e: MouseEvent) {
  if (!activeDrag) return;
  const dx = e.clientX - activeDrag.startMouseX;
  const dy = e.clientY - activeDrag.startMouseY;
  const rect = activeDrag.panel.getBoundingClientRect();
  // Keep at least the drag strip reachable on-screen (half the panel width).
  const minX = -(rect.width / 2) + 40;
  const maxX = window.innerWidth - rect.width / 2 - 40;
  const maxY = window.innerHeight - HIT_ZONE_HEIGHT;
  const nextLeft = Math.min(Math.max(activeDrag.startLeft + dx, minX), maxX);
  const nextTop = Math.min(Math.max(activeDrag.startTop + dy, 0), Math.max(maxY, 0));
  activeDrag.panel.style.left = `${nextLeft}px`;
  activeDrag.panel.style.top = `${nextTop}px`;
  e.preventDefault();
}

function onMouseUp() {
  if (!activeDrag) return;
  const zone = activeDrag.panel.querySelector<HTMLElement>(`#${HIT_ZONE_ID}`);
  if (zone) zone.style.cursor = 'grab';
  activeDrag = null;
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
}

function instrumentPanel(panel: HTMLElement) {
  if (instrumented.has(panel)) return;
  instrumented.add(panel);
  ensureHitZone(panel);
}

/** Scan for panels already in the DOM at install time. */
function scanExisting(root: HTMLElement | Document = document) {
  root.querySelectorAll(`.${DRAG_CLASS}`).forEach((el) => instrumentPanel(el as HTMLElement));
}

/**
 * Install the global drag system exactly once (module import side effect —
 * imported by src/main.tsx after React mounts).
 */
export function installModalDrag(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;

  document.addEventListener('mousedown', onMouseDown, { capture: true });
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((n) => {
        if (!(n instanceof HTMLElement)) return;
        if (n.classList.contains(DRAG_CLASS)) instrumentPanel(n);
        n.querySelectorAll?.(`.${DRAG_CLASS}`).forEach((el) => instrumentPanel(el as HTMLElement));
      });
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  scanExisting();
}

/** Test-only teardown (never called in production). */
export function _uninstallModalDragForTests(): void {
  if (!installed) return;
  installed = false;
  document.removeEventListener('mousedown', onMouseDown, { capture: true });
  document.removeEventListener('mousemove', onMouseMove);
  document.removeEventListener('mouseup', onMouseUp);
}
